import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { User, Lock, Camera, Save } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { apiService } from '../services/apiService';
import LoadingSpinner from '../components/UI/LoadingSpinner';
import toast from 'react-hot-toast';

const ProfilePage = () => {
  const { user, updateUser } = useAuth();
  const [activeTab, setActiveTab] = useState('profile');
  const [isUpdating, setIsUpdating] = useState(false);

  const {
    register: registerProfile,
    handleSubmit: handleSubmitProfile,
    formState: { errors: profileErrors },
  } = useForm({
    defaultValues: {
      name: user?.name || '',
      surname: user?.surname || '',
      entity: user?.entity || '',
      email: user?.email || '',
    },
  });

  const {
    register: registerPassword,
    handleSubmit: handleSubmitPassword,
    formState: { errors: passwordErrors },
    reset: resetPassword,
    watch,
  } = useForm();

  const newPassword = watch('newPassword');

  const onUpdateProfile = async (data) => {
    try {
      setIsUpdating(true);
      const response = await apiService.updateUser(user.id, data);
      updateUser(response.user);
      toast.success('Perfil atualizado com sucesso!');
    } catch (error) {
      const message = error.response?.data?.error || 'Erro ao atualizar perfil';
      toast.error(message);
    } finally {
      setIsUpdating(false);
    }
  };

  const onChangePassword = async (data) => {
    try {
      setIsUpdating(true);
      await apiService.changePassword(data.currentPassword, data.newPassword);
      resetPassword();
      toast.success('Password alterada com sucesso!');
    } catch (error) {
      const message = error.response?.data?.error || 'Erro ao alterar password';
      toast.error(message);
    } finally {
      setIsUpdating(false);
    }
  };

  const tabs = [
    { id: 'profile', name: 'Perfil', icon: User },
    { id: 'password', name: 'Password', icon: Lock },
  ];

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
          Perfil
        </h1>
        <p className="text-gray-600 dark:text-gray-400">
          Gerir as suas informações pessoais e definições de conta
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Sidebar */}
        <div className="lg:col-span-1">
          <div className="card p-6">
            {/* User Avatar */}
            <div className="text-center mb-6">
              <div className="w-20 h-20 bg-primary-500 rounded-full flex items-center justify-center mx-auto mb-3">
                <span className="text-2xl font-bold text-white">
                  {user?.name?.charAt(0)?.toUpperCase()}
                </span>
              </div>
              <h3 className="font-semibold text-gray-900 dark:text-white">
                {user?.name} {user?.surname}
              </h3>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {user?.entity || user?.role}
              </p>
            </div>

            {/* Navigation */}
            <nav className="space-y-2">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`w-full flex items-center space-x-3 px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                    activeTab === tab.id
                      ? 'bg-primary-100 text-primary-700 dark:bg-primary-900 dark:text-primary-300'
                      : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                  }`}
                >
                  <tab.icon className="w-5 h-5" />
                  <span>{tab.name}</span>
                </button>
              ))}
            </nav>
          </div>
        </div>

        {/* Main Content */}
        <div className="lg:col-span-3">
          {activeTab === 'profile' && (
            <div className="card p-6">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-6">
                Informações do Perfil
              </h2>

              <form onSubmit={handleSubmitProfile(onUpdateProfile)} className="space-y-6">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <div>
                    <label className="form-label">
                      Nome *
                    </label>
                    <input
                      type="text"
                      className="form-input"
                      {...registerProfile('name', { required: 'Nome é obrigatório' })}
                    />
                    {profileErrors.name && (
                      <p className="mt-1 text-sm text-red-600 dark:text-red-400">
                        {profileErrors.name.message}
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="form-label">
                      Apelido *
                    </label>
                    <input
                      type="text"
                      className="form-input"
                      {...registerProfile('surname', { required: 'Apelido é obrigatório' })}
                    />
                    {profileErrors.surname && (
                      <p className="mt-1 text-sm text-red-600 dark:text-red-400">
                        {profileErrors.surname.message}
                      </p>
                    )}
                  </div>
                </div>

                <div>
                  <label className="form-label">
                    Email
                  </label>
                  <input
                    type="email"
                    className="form-input bg-gray-50 dark:bg-gray-700"
                    disabled
                    {...registerProfile('email')}
                  />
                  <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                    O email não pode ser alterado. Contacte o administrador se necessário.
                  </p>
                </div>

                <div>
                  <label className="form-label">
                    Entidade
                  </label>
                  <input
                    type="text"
                    className="form-input"
                    placeholder="Ex: Bombeiros Voluntários, ICNF, etc."
                    {...registerProfile('entity')}
                  />
                </div>

                <div className="flex justify-end">
                  <button
                    type="submit"
                    disabled={isUpdating}
                    className="btn-primary flex items-center space-x-2 disabled:opacity-50"
                  >
                    {isUpdating ? (
                      <LoadingSpinner size="sm" />
                    ) : (
                      <Save className="w-4 h-4" />
                    )}
                    <span>{isUpdating ? 'A guardar...' : 'Guardar alterações'}</span>
                  </button>
                </div>
              </form>
            </div>
          )}

          {activeTab === 'password' && (
            <div className="card p-6">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-6">
                Alterar Password
              </h2>

              <form onSubmit={handleSubmitPassword(onChangePassword)} className="space-y-6">
                <div>
                  <label className="form-label">
                    Password atual *
                  </label>
                  <input
                    type="password"
                    className="form-input"
                    {...registerPassword('currentPassword', {
                      required: 'Password atual é obrigatória',
                    })}
                  />
                  {passwordErrors.currentPassword && (
                    <p className="mt-1 text-sm text-red-600 dark:text-red-400">
                      {passwordErrors.currentPassword.message}
                    </p>
                  )}
                </div>

                <div>
                  <label className="form-label">
                    Nova password *
                  </label>
                  <input
                    type="password"
                    className="form-input"
                    {...registerPassword('newPassword', {
                      required: 'Nova password é obrigatória',
                      minLength: {
                        value: 8,
                        message: 'Password deve ter pelo menos 8 caracteres',
                      },
                    })}
                  />
                  {passwordErrors.newPassword && (
                    <p className="mt-1 text-sm text-red-600 dark:text-red-400">
                      {passwordErrors.newPassword.message}
                    </p>
                  )}
                </div>

                <div>
                  <label className="form-label">
                    Confirmar nova password *
                  </label>
                  <input
                    type="password"
                    className="form-input"
                    {...registerPassword('confirmPassword', {
                      required: 'Confirmação de password é obrigatória',
                      validate: (value) =>
                        value === newPassword || 'As passwords não coincidem',
                    })}
                  />
                  {passwordErrors.confirmPassword && (
                    <p className="mt-1 text-sm text-red-600 dark:text-red-400">
                      {passwordErrors.confirmPassword.message}
                    </p>
                  )}
                </div>

                <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                  <h4 className="text-sm font-medium text-blue-900 dark:text-blue-300 mb-2">
                    Requisitos da password:
                  </h4>
                  <ul className="text-sm text-blue-700 dark:text-blue-400 space-y-1">
                    <li>• Pelo menos 8 caracteres</li>
                    <li>• Recomendado: mistura de letras, números e símbolos</li>
                  </ul>
                </div>

                <div className="flex justify-end">
                  <button
                    type="submit"
                    disabled={isUpdating}
                    className="btn-primary flex items-center space-x-2 disabled:opacity-50"
                  >
                    {isUpdating ? (
                      <LoadingSpinner size="sm" />
                    ) : (
                      <Lock className="w-4 h-4" />
                    )}
                    <span>{isUpdating ? 'A alterar...' : 'Alterar password'}</span>
                  </button>
                </div>
              </form>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProfilePage;
